import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { getDetailResults, getMyBets } from "../../../apiservices/CasionApi";
import CasinoResultContent from "./CasinoResultContent.jsx";
import CasinoBetsAddon from "./CasinoBetsAddon.jsx";
import "./feResult.css";

function pad(n) { return String(n).padStart(2, "0"); }
function fmtDateTime(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
}

// Map a raw casino_bets row → the shape CasinoBetsAddon expects.
// (Same mapping as d99-frontend CasinoResultModal.)
function mapBet(b) {
  const result = String(b.result_status || "").toLowerCase();
  const creditAmt = Number(b.credit_amt || 0);
  const exposer = Number(b.exposer || 0); // stored negative (locked liability)
  let win = 0;
  if (result === "won") win = creditAmt + exposer; // net profit
  else if (result === "lost") win = -creditAmt; // net loss
  const status = String(b.status || "").toLowerCase();
  return {
    nation: b.selection || b.player_name || "",
    rate: Number(b.odds) || b.odds,
    amount: Number(b.stake) || b.stake,
    win,
    date: fmtDateTime(b.created_at),
    ip: b.ip_address || "",
    browser: b.browser || "",
    side: String(b.type || "").toLowerCase(),
    deleted: status === "void" || result === "void" || status === "deleted",
  };
}

/**
 * Admin port of the frontend casino result modal (exact-same layout / content).
 * Fetches detail-results on open, renders game-specific content via switch.
 *
 * When `betsAddon` is true and a `userId` (the account-statement's target user)
 * is supplied, that user's bets for the round are rendered below the result.
 * All content is wrapped in `.fe-result-scope` so the ported frontend CSS is
 * isolated from the admin's own global casino-result styles.
 */
export default function CasinoResultModal({ show, onHide, gameType, mid, userId, title, betsAddon = false, bets }) {
  const [detailData, setDetailData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userBets, setUserBets] = useState(null);

  useEffect(() => {
    if (!show || !mid || !gameType) return;
    let cancelled = false;
    setLoading(true);
    setDetailData(null);

    getDetailResults(gameType, String(mid))
      .then((res) => {
        if (!cancelled && res) setDetailData(res);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [show, mid, gameType]);

  // Load the statement owner's real bets for this round.
  useEffect(() => {
    if (!betsAddon || !show || !mid) { setUserBets(null); return; }
    if (Array.isArray(bets)) { setUserBets(null); return; } // caller supplied bets
    if (!userId) { setUserBets([]); return; }

    let cancelled = false;
    getMyBets(String(userId), String(mid))
      .then((res) => {
        if (cancelled) return;
        const raw = res?.bets || res?.data || [];
        setUserBets(Array.isArray(raw) ? raw.map(mapBet) : []);
      })
      .catch(() => { if (!cancelled) setUserBets([]); });

    return () => { cancelled = true; };
  }, [betsAddon, show, mid, bets, userId]);

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{title || "Result"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="fe-result-scope">
          {loading ? (
            <div className="text-center p-4">
              <i className="fa fa-spinner fa-spin"></i>
            </div>
          ) : (
            <>
              <CasinoResultContent gameType={gameType} detailData={detailData} />
              {betsAddon && <CasinoBetsAddon bets={Array.isArray(bets) ? bets : userBets || []} />}
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
